import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Cpu, Cloud, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Sidebar admin health widget.
 *
 * Renders a compact status pill with:
 *   - a coloured dot (green/amber/red) reflecting overall health
 *   - available RAM in GB (or MB when < 1 GB)
 *   - the trailing-hour count of backend 5xx + upstream Cloudflare errors
 *
 * Polls `/api/health/detail` every 60 seconds. The endpoint is admin-
 * only server-side, so this component simply hides itself on 401/403
 * (via `enabled: isAdmin` + `retry: false` on the query). The parent
 * Sidebar passes `isAdmin` in — we don't re-derive it here to keep the
 * widget stateless and testable.
 */
export default function HealthWidget({ isAdmin }) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["health-detail"],
    queryFn: () => api.healthDetail(),
    enabled: !!isAdmin,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    // Fail quietly if the endpoint 401s (non-admin) or 5xxs — the widget
    // just renders its error state and keeps polling on the next tick.
    retry: false,
    staleTime: 30_000,
  });

  if (!isAdmin) return null;

  // Initial load — render a placeholder so the sidebar height is stable.
  if (isLoading && !data) {
    return (
      <div
        data-testid="health-widget-loading"
        className="mx-3 my-2 px-3 py-2 text-[10px] font-mono-display text-[var(--ink-muted)] border border-[var(--border)] bg-[var(--surface)]"
      >{tr("Checking backend health…")}</div>
    );
  }

  // Endpoint unreachable (backend down or CF 5xx). Fail loud but
  // narrow — the whole point of this widget is to signal outages.
  if (error || !data) {
    return (
      <div
        data-testid="health-widget-error"
        className="mx-3 my-2 px-3 py-2 border border-red-300 bg-red-50 dark:bg-red-950/30"
      >
        <div className="flex items-center gap-1.5 text-[10px] font-mono-display uppercase tracking-wider text-red-700">
          <AlertCircle className="w-3 h-3" />{tr("Backend unreachable")}</div>
      </div>
    );
  }

  const {
    status = "green",
    memory = {},
    backend_5xx_last_hour: backend5xx = 0,
    upstream_cloudflare_errors_last_hour: upstreamCf = 0,
  } = data;

  const dotBg = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  }[status] || "bg-neutral-400";

  const dotPulse = status === "green" ? "" : "animate-pulse";

  const memAvailable = memory.available_mb;
  const memText =
    memAvailable == null
      ? "—"
      : memAvailable >= 1024
      ? tr("{toFixed} GB free", { toFixed: (memAvailable / 1024).toFixed(1) })
      : tr("{memAvailable} MB free", { memAvailable });
  const memPctText = memory.used_pct != null ? tr("{used_pct}% used", { used_pct: memory.used_pct }) : "";
  // A cgroup reading counts reclaimable page cache, so a high
  // percentage is normal and not worth alarming about. The kill count
  // is the signal — and it is the one number that was only visible by
  // waiting for a request to fail and reading the Request Log.
  const oomKills = memory.oom_kills;
  const oomText =
    oomKills == null ? "" : oomKills === 0 ? tr("no OOM kills") : tr("{oomKills} OOM kills", { oomKills });

  const errorCount = backend5xx + upstreamCf;
  const cfLabel =
    errorCount === 0
      ? tr("No errors · 1h")
      : `${errorCount} error${errorCount === 1 ? "" : "s"} · 1h`;

  return (
    <div
      data-testid="health-widget"
      title={
        tr("Backend 5xx (last hour): {backend5xx}\nAnthropic Cloudflare hiccups (last hour): {upstreamCf}\n{value}", { backend5xx, upstreamCf, value: memory.total_mb
          ? tr("Memory: {available_mb} MB free of {total_mb} MB total ({used_pct}% used, {value}){value2}{value3}", { available_mb: memory.available_mb, total_mb: memory.total_mb, used_pct: memory.used_pct, value: memory.scope === "container" ? tr("this container") : tr("host — not this container"), value2: oomKills == null ? "" : tr("\nOOM kills: {oomKills}", { oomKills }), value3: memory.scope === "container"
              ? tr("\nA high percentage is normal — it includes reclaimable page cache. The OOM kill count is the real signal.")
              : "" })
          : tr("Memory info unavailable") })
      }
      className="mx-3 my-2 px-3 py-2 border border-[var(--border)] bg-[var(--surface)] space-y-1.5"
    >
      <div className="flex items-center gap-1.5 text-[10px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)]">
        {trRich("<0/>Backend health", null, [<span
          data-testid={`health-widget-dot-${status}`}
          className={`inline-block w-2 h-2 rounded-full ${dotBg} ${dotPulse}`}
          aria-label={tr("Backend status: {status}", { status })}
        />])}</div>
      <div
        data-testid="health-widget-memory"
        className="flex items-center gap-1.5 text-[11px] text-[var(--ink)]"
      >
        <Cpu className="w-3 h-3 text-[var(--ink-muted)] shrink-0" />
        <span className="truncate" title={memPctText}>
          {memText}
          {oomText ? (
            <span
              className={
                oomKills > 0 ? "text-red-700 ml-1" : "text-[var(--ink-muted)] ml-1"
              }
            >
              · {oomText}
            </span>
          ) : memPctText ? (
            <span className="text-[var(--ink-muted)] ml-1">· {memPctText}</span>
          ) : null}
        </span>
      </div>
      <div
        data-testid="health-widget-cloudflare"
        className={`flex items-center gap-1.5 text-[11px] ${
          errorCount === 0 ? "text-[var(--ink)]" : "text-amber-700"
        }`}
      >
        {errorCount === 0 ? (
          <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
        ) : (
          <Cloud className="w-3 h-3 shrink-0" />
        )}
        <span className="truncate">{cfLabel}</span>
      </div>
    </div>
  );
}
